import { useEffect, useRef, useState } from "react";
import {
  requestSettingsSnapshot,
  sendSettingsSet,
  subscribeSettingsSnapshots,
} from "../services/settingsChannel";
import { setTimerFollowing, timerMutedFor } from "../audio/audioOutput";

// The firmware's own mute (KhronosFirmware settings channel), saved in the
// timer's NVS — so whatever this hook changes it puts back afterwards.
export const TIMER_MUTE_KEY = "audio.muted";

/**
 * Applies the "Sound plays on" choice (issue #45) to your timers following a
 * live session.
 *
 * The timer side is edge-triggered: the mute is written when a timer is first
 * seen and whenever the choice changes, never re-asserted from later
 * snapshots, so the remote's own Mute key still works mid-session. A timer's
 * mute from before the session is recorded from its first snapshot and put
 * back when the session ends (`active` goes false) or the flow unmounts. A
 * timer dropping off Wi-Fi mid-session is not an end: it keeps its record and
 * gets the choice re-applied when it comes back.
 *
 * Firmware without the settings channel never sends a snapshot, so it is
 * simply left alone.
 *
 * @param {number[]} deviceIds your own timers attached to the session now
 * @param {string} output "both" | "app" | "device"
 * @param {boolean} active false once the session has ended
 */
export default function useTimerAudioRouting(deviceIds, output, active = true) {
  // id -> { original, current, applied, wrote }
  const timers = useRef(new Map());
  const [learned, setLearned] = useState(0);
  const key = active ? [...deviceIds].map(Number).sort((a, b) => a - b).join(",") : "";

  useEffect(() => {
    setTimerFollowing(key !== "");
  }, [key]);

  // Record each timer's mute; ask attached ones for a fresh snapshot.
  useEffect(() => {
    if (!key) return undefined;
    const ids = key.split(",").map(Number);
    const unsub = subscribeSettingsSnapshots((s) => {
      const id = Number(s?.device_id);
      // A cache replay is the last state of an offline timer, not its current one.
      if (!ids.includes(id) || s.cached || !Array.isArray(s.settings)) return;
      const item = s.settings.find((x) => x?.key === TIMER_MUTE_KEY);
      if (typeof item?.value !== "boolean") return;
      const t = timers.current.get(id);
      if (t) {
        t.current = item.value;
      } else {
        timers.current.set(id, { original: item.value, current: item.value, applied: undefined, wrote: false });
        setLearned((n) => n + 1);
      }
    });
    ids.forEach((id) => {
      // Back after a drop-out: apply the choice again once it reports in.
      const t = timers.current.get(id);
      if (t) t.applied = undefined;
      requestSettingsSnapshot(id);
    });
    return unsub;
  }, [key]);

  const target = timerMutedFor(output);
  useEffect(() => {
    if (!key) return;
    const ids = key.split(",").map(Number);
    for (const [id, t] of timers.current) {
      if (!ids.includes(id) || t.applied === target) continue;
      t.applied = target;
      if (t.current === target) continue;
      t.wrote = true;
      sendSettingsSet(id, TIMER_MUTE_KEY, target).catch(() => {});
    }
  }, [key, target, learned]);

  // Put each timer's mute back at the end of the session.
  useEffect(() => {
    if (active) return undefined;
    restoreAll(timers.current);
    return undefined;
  }, [active]);
  useEffect(() => {
    const map = timers.current;
    return () => {
      restoreAll(map);
      setTimerFollowing(false);
    };
  }, []);
}

function restoreAll(map) {
  for (const [id, t] of map) {
    if (t.wrote) sendSettingsSet(id, TIMER_MUTE_KEY, t.original).catch(() => {});
  }
  map.clear();
}
